// Code-decoding logic ported from the original Nailor PLUS.html (~L327-555).
// Pure functions only — no React, no Firebase. UI lives in components/.

import type {
  Model,
  ModelTemplate,
  ModelTemplateAccessory,
  ModelTemplateField,
} from "@/modules/plus/plus.schema";

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

// Canonicalize a code-like input: uppercase, dashes for separators, no spaces.
// Examples: "6500 o 9 x 9" -> "6500-O-9X9"; "6500–O" (en-dash) -> "6500-O".
export const norm = (s: string): string =>
  String(s)
    .toUpperCase()
    .replace(/[–—−]/g, "-")
    .replace(/\s*[xX×]\s*(?=\d)/g, "X")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// Lighter normalization for individual codes (preserves dashes, drops spaces).
export const normCode = (s: string): string =>
  String(s).toUpperCase().replace(/\s+/g, "").replace(/[–—−]/g, "-");

// Render a token nicely for display. e.g. "0606" -> '6" × 6"', "08" -> '8"'.
export const pretty = (token: string): string => {
  const fourDigit = token.match(/^(\d{2})(\d{2})$/);
  if (fourDigit) {
    const a = Number.parseInt(fourDigit[1] ?? "0", 10);
    const b = Number.parseInt(fourDigit[2] ?? "0", 10);
    if (a <= 48 && b <= 48 && fourDigit[1] !== "00") {
      return `${a}" × ${b}"`;
    }
  }
  const wXh = token.match(/^(\d{1,2})X(\d{1,2})$/);
  if (wXh) {
    return `${wXh[1]}" × ${wXh[2]}"`;
  }
  if (/^\d{1,2}$/.test(token)) {
    return `${Number.parseInt(token, 10)}"`;
  }
  return token;
};

// ---------------------------------------------------------------------------
// Size handling
// ---------------------------------------------------------------------------

// Expand a single value string into the alternate forms a code might appear as:
//   "6 x 6"    -> ["6X6", "0606", '6X6"']
//   "08"       -> ["08", "08", "8"]
//   "24 x 24 (default)" -> ["24X24", "2424", '24X24"']
// The original JS dropped the trailing `"` form for "08" cases — preserved here.
export const sizeForms = (raw: string): string[] => {
  const s = raw
    .toUpperCase()
    .replace(/\(DEFAULT\)/g, "")
    .trim();
  const out: string[] = [];

  const wXh = s.match(/^(\d{1,2})\s*X\s*(\d{1,2})$/);
  if (wXh) {
    const a = wXh[1] ?? "";
    const b = wXh[2] ?? "";
    out.push(`${a}X${b}`);
    out.push(`0${a}`.slice(-2) + `0${b}`.slice(-2));
    out.push(`${a}X${b}"`);
    return out;
  }

  if (/^\d{1,2}$/.test(s)) {
    out.push(s);
    out.push(`0${s}`.slice(-2));
    out.push(String(Number.parseInt(s, 10)));
    return out;
  }

  if (s) {
    out.push(s.replace(/\s+/g, ""));
  }
  return out;
};

// A "code field" has a small enumerated set of short codes (AW, RND, 4A, -O).
// A "free-form" field has `values` and accepts size-like or label-like tokens.
// Mixed-case keys like "Imperial (inches)" are size-group LABELS, not codes.
export const isCodeField = (field: ModelTemplateField): boolean => {
  const keys = Object.keys(field.codes ?? {});
  if (!keys.length) {
    return false;
  }
  // Real ordering codes print as uppercase letters / digits / a small alphabet.
  return keys.every((k) => k.length <= 6 && /^[A-Z0-9/().–—-]+$/.test(k));
};

interface AllowedSizes {
  allowed: Record<string, string>; // token -> group label
  hasDefault: Record<string, true>;
}

// Free-form size/module field: parse the `values` description into the set of
// tokens that are acceptable, plus per-token group labels ("Imperial (inches)").
export const allowedSizes = (field: ModelTemplateField): AllowedSizes => {
  const allowed: Record<string, string> = {};
  const hasDefault: Record<string, true> = {};
  const groups: Record<string, string> = {};

  if (field.codes) {
    for (const [g, v] of Object.entries(field.codes)) {
      groups[g] = String(v);
    }
  }
  if (field.values) {
    // "Imperial (inches): 12 x 12, 24 x 24 (default). Metric (mm): 300 x 300"
    // also accepts the bare-list form: "04, 05, 06"
    for (const seg of String(field.values).split(/\.\s*/)) {
      const m = seg.match(/^([^:]{0,30}):\s*(.+)$/);
      if (m) {
        groups[m[1]?.trim() ?? ""] = m[2] ?? "";
      } else if (seg.trim()) {
        const cur = groups[""] ?? "";
        groups[""] = cur ? `${cur}, ${seg}` : seg;
      }
    }
  }

  for (const [groupLabel, valueList] of Object.entries(groups)) {
    for (const v of valueList.split(",")) {
      const isDef = /default/i.test(v);
      for (const f of sizeForms(v)) {
        if (f) {
          allowed[f] = groupLabel;
          if (isDef) {
            hasDefault[f] = true;
          }
        }
      }
    }
  }
  return { allowed, hasDefault };
};

// ---------------------------------------------------------------------------
// Field matching
// ---------------------------------------------------------------------------

interface FieldMatchResult {
  meaning: string;
  isDefault: boolean;
  warning?: string;
}

// Try to interpret `token` against `field`. Returns null when no rule matches.
// Order: exact code → 0/O confusion → alphabetic+numeric "specify" codes → size lookup → generic "specify" range.
export const fieldMatch = (field: ModelTemplateField, token: string): FieldMatchResult | null => {
  if (isCodeField(field)) {
    const codes = field.codes ?? {};
    for (const [rawCode, meaning] of Object.entries(codes)) {
      const nk = normCode(rawCode).replace(/^-+/, ""); // "-O" suffix codes tokenize as "O"

      if (nk === token || (nk === "O" && token === "0") || (nk === "0" && token === "O")) {
        return { meaning, isDefault: /default/i.test(meaning) };
      }

      // Alphabetic code with numeric suffix, e.g. SR08 = SR collar, 8".
      if (/^[A-Z]{1,4}$/.test(nk) && token.startsWith(nk)) {
        const tail = token.slice(nk.length);
        if (/^\d{1,2}$/.test(tail)) {
          return {
            meaning: `${meaning} • ${Number.parseInt(tail, 10)}"`,
            isDefault: false,
          };
        }
      }
    }
    return null;
  }

  // Free-form fields. If the field name hints at a size/module/neck dimension,
  // run the allowedSizes pipeline. Otherwise try the generic "specify" matcher.
  const name = (field.name ?? "").toLowerCase();
  if (/size|module|neck|slot|length/.test(name)) {
    const az = allowedSizes(field);
    if (token in az.allowed) {
      const grp = az.allowed[token];
      return {
        meaning: pretty(token) + (grp ? ` (${grp})` : ""),
        isDefault: Boolean(az.hasDefault[token]),
      };
    }
    // Plausible size shape that wasn't enumerated — accept with a warning so
    // the user sees we recognized the shape but didn't authoritatively match.
    if (/^\d{1,2}$/.test(token) || /^\d{1,2}X\d{1,2}$/.test(token) || /^\d{4}$/.test(token)) {
      return {
        meaning: pretty(token),
        isDefault: false,
        warning: "Not in the listed sizes for this series — verify against catalog",
      };
    }
  }

  // Generic "specify" range, e.g. Transition Collar "SR04 to SR18" matches SR08.
  const sm = token.match(/^([A-Z]{1,4})(\d{1,2})$/);
  if (
    sm &&
    field.values &&
    String(field.values)
      .toUpperCase()
      .includes(sm[1] ?? "")
  ) {
    return {
      meaning: `${field.name} • ${sm[1]}, ${Number.parseInt(sm[2] ?? "0", 10)}" — ${field.values}`,
      isDefault: false,
    };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Index + main decode entry point
// ---------------------------------------------------------------------------

interface IndexEntry {
  template: number;
  code: string;
  meaning: string;
}

// Build a lookup: normalized model code → { template index, original code, meaning }.
export const buildIndex = (templates: readonly ModelTemplate[]): Record<string, IndexEntry> => {
  const idx: Record<string, IndexEntry> = {};
  templates.forEach((g, gi) => {
    const modelField = g.fields[0];
    if (!modelField?.codes) {
      return;
    }
    for (const [code, meaning] of Object.entries(modelField.codes)) {
      const n = normCode(code);
      if (!(n in idx)) {
        idx[n] = { template: gi, code, meaning };
      }
    }
  });
  return idx;
};

export interface DecodedField {
  name: string;
  token: string;
  meaning: string;
  isDefault: boolean;
  warning?: string;
}

export interface DecodedSuccess {
  ok: true;
  kind: "full";
  model: string;
  modelMeaning: string;
  series: string;
  heading: string;
  page?: string;
  example: string;
  fields: DecodedField[];
  accessories: ModelTemplateAccessory[];
  unknown: string[];
  warnings: string[];
  normalized: string;
  db: Model | null;
  dbFallback?: { from: string; db: Model };
}

export interface DecodedDbOnly {
  ok: true;
  kind: "dbOnly";
  model: string;
  db: Model;
  leftover: string[];
  warnings: string[];
  normalized: string;
}

export interface DecodedNoModel {
  ok: false;
  error: "no_model";
  firstToken: string;
  candidates: string[];
  suggestions: string[];
  normalized: string;
}

export interface DecodedEmpty {
  ok: false;
  error: "empty";
}

export type DecodeResult = DecodedSuccess | DecodedDbOnly | DecodedNoModel | DecodedEmpty;

// Find the longest model-code prefix of `s` that exists in `idx`.
const findLongestPrefix = (s: string, codes: readonly string[]): string | null => {
  // codes are pre-sorted longest-first by caller
  for (const c of codes) {
    if (s === c || (s.startsWith(c) && s[c.length] === "-")) {
      return c;
    }
  }
  return null;
};

// Find a database-only model (no template entry) that prefixes `s`.
const findDbOnlyModel = (
  s: string,
  modelsMap: Readonly<Record<string, Model>> | undefined,
): string | null => {
  if (!modelsMap) {
    return null;
  }
  const dbCodes = Object.keys(modelsMap).sort((a, b) => b.length - a.length);
  for (const raw of dbCodes) {
    const d = normCode(raw);
    if (s === d || (s.startsWith(d) && s[d.length] === "-")) {
      return raw;
    }
  }
  return null;
};

// When no model matches: best-effort guesses to help the user.
const buildNoModelHints = (
  s: string,
  templates: readonly ModelTemplate[],
  modelCodes: readonly string[],
): { firstToken: string; candidates: string[]; suggestions: string[] } => {
  const firstToken = s.split("-")[0] ?? "";
  const candidates: string[] = [];
  for (const g of templates) {
    // templates whose 2nd field's codes contain the first token — likely the user
    // gave a 2nd-field option without a model prefix.
    const f2 = g.fields[1];
    const hit = f2?.codes && Object.keys(f2.codes).some((k) => normCode(k) === firstToken);
    if (hit) {
      const f1Codes = g.fields[0]?.codes ?? {};
      for (const m of Object.keys(f1Codes)) {
        candidates.push(m);
      }
    }
  }
  const suggestions = modelCodes.filter((c) => c.startsWith(firstToken)).slice(0, 8);
  return { firstToken, candidates: candidates.slice(0, 12), suggestions };
};

// Pick a photo to display alongside the result. Preference order:
//   1) the resolved model's own exact-match photo (already on result.db),
//   2) an exact-match photo from a sibling model in the same series,
//   3) any photo from a sibling model in the same series.
const findPhotoFallback = (
  primary: Model | null,
  template: ModelTemplate,
  modelsMap: Readonly<Record<string, Model>>,
): { from: string; db: Model } | undefined => {
  if (primary?.photo_url && primary.photo_match === "exact") {
    return undefined; // primary is good enough
  }
  const mateCodes = Object.keys(template.fields[0]?.codes ?? {});
  let any: { from: string; db: Model } | undefined;
  for (const mateCode of mateCodes) {
    const mate = modelsMap[mateCode] ?? modelsMap[normCode(mateCode)];
    if (!mate?.photo_url) {
      continue;
    }
    if (mate.photo_match === "exact") {
      return { from: mateCode, db: mate };
    }
    any ??= { from: mateCode, db: mate };
  }
  if (!primary?.photo_url) {
    return any;
  }
  return undefined;
};

export const decode = (
  input: string,
  templates: readonly ModelTemplate[],
  modelsMap?: Readonly<Record<string, Model>>,
): DecodeResult => {
  const idx = buildIndex(templates);
  const s = norm(input);
  if (!s) {
    return { ok: false, error: "empty" };
  }

  const modelCodes = Object.keys(idx).sort((a, b) => b.length - a.length);
  const found = findLongestPrefix(s, modelCodes);
  const dbOnly = !found ? findDbOnlyModel(s, modelsMap) : null;

  if (!found && !dbOnly) {
    return {
      ok: false,
      error: "no_model",
      ...buildNoModelHints(s, templates, modelCodes),
      normalized: s,
    };
  }

  if (dbOnly && !found) {
    const db = modelsMap?.[dbOnly];
    const rest = s.slice(normCode(dbOnly).length).replace(/^-/, "");
    return {
      ok: true,
      kind: "dbOnly",
      model: dbOnly,
      db: db!,
      leftover: rest ? rest.split("-") : [],
      warnings: ["No ordering key extracted for this series yet — decoded from website data only."],
      normalized: s,
    };
  }

  // Full template match. `found` is the normalized model code from the idx.
  const entry = idx[found!]!;
  const g = templates[entry.template]!;
  const tokens = s.slice(found!.length).replace(/^-/, "").split("-").filter(Boolean);
  const db = modelsMap?.[entry.code] ?? modelsMap?.[normCode(entry.code)] ?? null;
  const dbFallback = modelsMap ? findPhotoFallback(db, g, modelsMap) : undefined;

  const decodedFields: DecodedField[] = [];
  const decodedAccessories: ModelTemplateAccessory[] = [];
  const unknown: string[] = [];
  const warnings: string[] = [];

  let fi = 1; // skip model field
  for (const token of tokens) {
    let placed = false;
    let probe = fi;
    while (probe < g.fields.length) {
      const f = g.fields[probe]!;
      const m = fieldMatch(f, token);
      if (m) {
        decodedFields.push({
          name: f.name,
          token,
          meaning: m.meaning,
          isDefault: m.isDefault,
          warning: m.warning,
        });
        if (m.warning) {
          warnings.push(`"${token}": ${m.warning}`);
        }
        fi = probe + 1;
        placed = true;
        break;
      }
      probe++;
    }
    if (!placed) {
      const accessory = (g.accessories ?? []).find((a) => normCode(a.code) === token);
      if (accessory) {
        decodedAccessories.push(accessory);
      } else {
        unknown.push(token);
      }
    }
  }

  if (unknown.length) {
    warnings.push(
      `Unrecognized segment(s): ${unknown.join(", ")} — check the catalog key${g.catalog_page ? ` (p. ${g.catalog_page})` : ""}`,
    );
  }

  return {
    ok: true,
    kind: "full",
    model: entry.code,
    modelMeaning: entry.meaning,
    series: g.series_id,
    heading: g.heading,
    page: g.catalog_page,
    example: g.example,
    fields: decodedFields,
    accessories: decodedAccessories,
    unknown,
    warnings,
    normalized: s,
    db,
    dbFallback,
  };
};
